import { useMemo } from "react";
import { useLanguage } from "./useLanguage";
// Language type is defined inline
type Language = "en" | "sv";

type TranslationModule = Record<string, string>;

/**
 * Hook to get translations for a specific module/page
 * 
 * @example
 * ```tsx
 * const t = useTranslation(() => import(`@/i18n/locales/${language}/dashboard`));
 * return <h1>{t.title}</h1>;
 * ```
 */
export function useTranslation<T extends TranslationModule>(
  _importFn: (language: Language) => Promise<{ [key: string]: T }>
): T {
  // In a real implementation, you'd want to handle async loading
  // For now, we'll use a synchronous approach with dynamic imports
  // This is a simplified version - in production you'd want proper async handling
  
  // This is a placeholder - actual implementation would handle async loading
  // For now, we'll use a simpler approach with direct imports
  throw new Error("useTranslation with dynamic imports not yet implemented. Use usePageTranslation instead.");
}

/**
 * Hook to get translations for a specific page/feature
 * This is the recommended approach for scalability
 * 
 * @example
 * ```tsx
 * import { dashboardTranslations } from "@/i18n/locales/en/dashboard";
 * import { dashboardTranslations as thDashboard } from "@/i18n/locales/th/dashboard";
 * 
 * const t = usePageTranslation({
 *   en: dashboardTranslations,
 *   th: thDashboard,
 * });
 * 
 * return <h1>{t.title}</h1>;
 * ```
 */
export function usePageTranslation(translations: {
  en: TranslationModule;
  th: TranslationModule;
}): TranslationModule {
  const { language } = useLanguage();
  // Use useMemo to ensure we return a stable reference
  // Only depend on language, not translations object (which is constant)
  return useMemo(() => translations[language], [language]);
}

